import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Upload, Play, Download, Trash2, AlertCircle, FileText, BarChart3, Package, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { supabase } from '@/integrations/supabase/client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { BotMergeAnalysisChart } from '@/components/admin/merge-bots/BotMergeAnalysisChart';
import { BotMergeAnalysisStats } from '@/components/admin/merge-bots/BotMergeAnalysisStats';
import { TradingHistoryTable } from './TradingHistoryTable';
import { LossStreakControls } from './LossStreakControls';
import { WinProtectControls } from './WinProtectControls';
import { FileParametersPanel } from './FileParametersPanel';
import { IndividualFilePerformance } from './IndividualFilePerformance';
import { PercentageCalculatorPanel } from './PercentageCalculatorPanel';
import { ManualTPSLModal } from './ManualTPSLModal';
import { CsvConfigUpload, CsvConfigRow } from './CsvConfigUpload';
import { PositionSequenceFinder } from './PositionSequenceFinder';
import { CsvProjectManager } from './CsvProjectManager';
import { FileProjectSelector } from './FileProjectSelector';
import { AnalysisParams, getDefaultParams } from './types';
import { AntiLossStreakService } from '@/services/anti-loss-streak-service';
import { useAuth } from '@/contexts/AuthContext';
import Papa from 'papaparse';
import JSZip from 'jszip';
import { formatCurrency } from '@/utils/formatters';
import { parseDecimal } from '@/lib/numberParsing';

interface CsvUpload {
  id: string;
  filename: string;
  file_size: number;
  storage_path: string;
  headers: string[];
  sample_rows: any[];
  created_at: string;
}

interface CsvAnalysis {
  id: string;
  name: string;
  upload_id: string;
  user_id: string;
  params: {
    tp_usd: number;
    sl_usd: number;
    quantity_base: number;
    initial_capital: number;
    selected_qty_values: string[];
    loss_streak_enabled?: boolean;
    max_consecutive_losses?: number;
    resume_after_win?: boolean;
    win_protect_enabled?: boolean;
    win_protect_start_wins?: number;
    win_protect_max_wins?: number;
    win_protect_include_nth_win?: boolean;
  };
  created_at: string;
}

interface ParsedCsvRow {
  timestamp: string;
  pnl: number;
  signal: string;
  price: number;
  position_size: number;
  position_size_raw: string;
  position_value?: number;
  type?: string;
  runup_usd?: number;
  runup_percent?: number;
  drawdown_usd?: number;
  drawdown_percent?: number;
  cumulative_pnl?: number;
  cumulative_percent?: number;
  isSkipped?: boolean;
  skipReason?: string;
  filename?: string;
  symbol?: string; // Add symbol extraction
}

interface FileContribution {
  filename: string;
  fileId: string;
  totalPnl: number;
  tradeCount: number;
  winningTrades: number;
  losingTrades: number;
  winRate: number;
  avgWin: number;
  avgLoss: number;
  profitFactor: number;
  maxTP: number;
  maxSL: number;
  maxDrawdown: number;
  maxDrawdownPercent: number;
  dateRange: {
    start: string;
    end: string;
  };
}

interface AnalysisResults {
  equityData: Array<{
    timestamp: string;
    cumulative_pnl: number;
    total_pnl: number;
    bot_contributions: Record<string, number>;
  }>;
  statistics: {
    totalTrades: number;
    winningTrades: number;
    losingTrades: number;
    winRate: number;
    totalPnl: number;
    avgWin: number;
    avgLoss: number;
    profitFactor: number;
    maxDrawdown: number;
    maxDrawdownPercent: number;
    riskRewardRatio: number;
  };
  fileContributions: FileContribution[];
  tradingHistory: Array<ParsedCsvRow & {
    index: number;
    normalized_pnl: number;
    net_pnl_percent: number;
    cumulative_pnl: number;
    cumulative_percent: number;
    isSkipped?: boolean;
    skipReason?: string;
  }>;
}

export const CsvAnalysisPanel: React.FC = () => {
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [selectedForDeletion, setSelectedForDeletion] = useState<Set<string>>(new Set());
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [csvData, setCsvData] = useState<ParsedCsvRow[]>([]);
  const [uniquePositionSizes, setUniquePositionSizes] = useState<string[]>([]);
  const [perFilePositionSizes, setPerFilePositionSizes] = useState<Map<string, string[]>>(new Map());
  const [selectedPositionSizes, setSelectedPositionSizes] = useState<Set<string>>(new Set());  
  const [analysisResults, setAnalysisResults] = useState<AnalysisResults | null>(null);
  const [isAnalysisRunning, setIsAnalysisRunning] = useState(false);
  const [isExportingIndividual, setIsExportingIndividual] = useState(false);
  const [fileDataMap, setFileDataMap] = useState<Map<string, ParsedCsvRow[]>>(new Map());
  const [perFileParams, setPerFileParams] = useState<Map<string, AnalysisParams>>(new Map());
  
  // CSV Configuration state
  const [csvConfig, setCsvConfig] = useState<CsvConfigRow[]>([]);
  
  // Global defaults with localStorage persistence
  const [globalDefaults, setGlobalDefaults] = useState<AnalysisParams>(() => {
    const saved = localStorage.getItem('user-csv-analysis-global-defaults');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.warn('Failed to parse saved global defaults:', e);
      }
    }
    return getDefaultParams();
  });
  
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Save global defaults to localStorage
  useEffect(() => {
    localStorage.setItem('user-csv-analysis-global-defaults', JSON.stringify(globalDefaults));
  }, [globalDefaults]);

  // Helper functions for localStorage persistence
  const getFileParamsKey = (upload: CsvUpload) => {
    // Use only filename for consistency, ignore file size to handle re-uploads
    return `csv-file-params-${upload.filename.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
  };

  const saveFileParams = (fileId: string, params: AnalysisParams) => {
    const upload = uploads.find(u => u.id === fileId);
    if (upload) {
      const key = getFileParamsKey(upload);
      localStorage.setItem(key, JSON.stringify(params));
      console.log(`✅ Saved params for file: ${upload.filename}`);
    }
  };

  const loadFileParams = (fileId: string): AnalysisParams | null => {
    const upload = uploads.find(u => u.id === fileId);
    if (!upload) return null;

    // Try new key format first
    const newKey = getFileParamsKey(upload);
    let saved = localStorage.getItem(newKey);
    if (saved) {
      try {
        const params = JSON.parse(saved);
        console.log(`🔄 Loaded saved params for file: ${upload.filename} (new key)`);
        return params;
      } catch (e) {
        console.warn('Failed to parse saved file params (new key):', e);
      }
    }

    // Try legacy key format with file size
    const legacyKey = `csv-file-params-${upload.filename}-${upload.file_size}`;
    saved = localStorage.getItem(legacyKey);
    if (saved) {
      try {
        const params = JSON.parse(saved);
        console.log(`🔄 Loaded saved params for file: ${upload.filename} (legacy key)`);
        // Migrate to new key format
        localStorage.setItem(newKey, saved);
        console.log(`🔄 Migrated legacy config to new key for: ${upload.filename}`);
        return params;
      } catch (e) {
        console.warn('Failed to parse saved file params (legacy key):', e);
      }
    }

    // Scan all localStorage keys for any potential matches
    const sanitizedFilename = upload.filename.replace(/[^a-zA-Z0-9._-]/g, '_');
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('csv-file-params-') && 
          (key.includes(upload.filename) || key.includes(sanitizedFilename))) {
        const scanSaved = localStorage.getItem(key);
        if (scanSaved) {
          try {
            const params = JSON.parse(scanSaved);
            console.log(`🔄 Found saved params for file: ${upload.filename} (scan key: ${key})`);
            // Migrate to new key format
            localStorage.setItem(newKey, scanSaved);
            console.log(`🔄 Migrated scan config to new key for: ${upload.filename}`);
            return params;
          } catch (e) {
            console.warn(`Failed to parse scanned file params for key ${key}:`, e);
          }
        }
      }
    }

    return null;
  };

  // Helper functions for parameter management
  const updateGlobalDefaults = (params: AnalysisParams) => {
    setGlobalDefaults(params);
  };

  const updateFileParams = (fileId: string, params: AnalysisParams) => {
    setPerFileParams(prev => new Map(prev).set(fileId, params));
    saveFileParams(fileId, params); // Auto-save to localStorage
  };

  const getFileParams = (fileId: string): AnalysisParams => {
    // First check in-memory state
    const inMemoryParams = perFileParams.get(fileId);
    if (inMemoryParams) return inMemoryParams;
    
    // Then check localStorage
    const savedParams = loadFileParams(fileId);
    if (savedParams) {
      // Load into state and return
      setPerFileParams(prev => new Map(prev).set(fileId, savedParams));
      return savedParams;
    }
    
    // Finally fallback to global defaults
    return globalDefaults;
  };

  const copyParamsToAll = (sourceFileId: string) => {
    const sourceParams = perFileParams.get(sourceFileId) || globalDefaults;
    const newMap = new Map(perFileParams);
    for (const fileId of selectedFiles) {
      if (fileId !== sourceFileId) {
        newMap.set(fileId, { ...sourceParams });
      }
    }
    setPerFileParams(newMap);
  };

  // Handler for applying position sequences
  const handleApplySequence = (sequence: string[]) => {
    const newMap = new Map(perFileParams);
    for (const fileId of selectedFiles) {
      const currentParams = getFileParams(fileId);
      const updatedParams = {
        ...currentParams,
        selected_qty_values: sequence,
        loss_streak_enabled: true, // Enable martingale when applying sequence
        max_consecutive_losses: currentParams.max_consecutive_losses || 5,
        resume_after_win: currentParams.resume_after_win || false,
      };
      newMap.set(fileId, updatedParams);
      saveFileParams(fileId, updatedParams);
    }
    setPerFileParams(newMap);
  };

  // Handler for bulk applying position size multiplier
  const handleBulkApplyMultiplier = (multiplier: number, fileIds: string[]) => {
    const newMap = new Map(perFileParams);
    for (const fileId of fileIds) {
      const currentParams = getFileParams(fileId);
      const updatedParams = {
        ...currentParams,
        position_size_multiplier: multiplier,
      };
      newMap.set(fileId, updatedParams);
      saveFileParams(fileId, updatedParams);
    }
    setPerFileParams(newMap);
  };

  // Fetch uploaded CSV files
  const { data: uploads = [] } = useQuery({
    queryKey: ['csv-uploads', user?.id, selectedProjectId],
    queryFn: async () => {
      if (!user?.id) return [];
      
      let query = supabase
        .from('user_csv_uploads')
        .select('*')
        .eq('user_id', user.id);
      
      // Filter by project if one is selected
      if (selectedProjectId) {
        query = query.eq('project_id', selectedProjectId);
      } else if (selectedProjectId === null) {
        // Show all files when "All Files" is selected
        // No additional filter needed
      }
        
      const { data, error } = await query.order('created_at', { ascending: false });
        
      if (error) throw error;
      return data as CsvUpload[];
    },
    enabled: !!user?.id,
  });

  // Auto-load saved configurations when uploads are loaded or selectedFiles change
  useEffect(() => {
    if (uploads.length > 0 && selectedFiles.size > 0) {
      const newMap = new Map(perFileParams);
      let hasChanges = false;
      
      for (const fileId of selectedFiles) {
        // Only load if not already in memory
        if (!perFileParams.has(fileId)) {
          const savedParams = loadFileParams(fileId);
          if (savedParams) {
            newMap.set(fileId, savedParams);
            hasChanges = true;
          }
        }
      }
      
      if (hasChanges) {
        setPerFileParams(newMap);
        console.log(`📁 Auto-loaded configurations for ${Array.from(selectedFiles).length} selected files`);
      }
    }
  }, [uploads, selectedFiles, perFileParams]);

  // Fetch saved analyses for selected files with fallback to all user analyses
  const { data: savedAnalyses = [] } = useQuery({
    queryKey: ['csv-analyses', Array.from(selectedFiles).sort().join(','), user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      let analyses = [];
      
      // First try to get analyses for selected files
      if (selectedFiles.size > 0) {
        for (const fileId of selectedFiles) {
          const { data, error } = await supabase
            .from('user_csv_analyses')
            .select('*')
            .eq('upload_id', fileId)
            .order('created_at', { ascending: false });
            
          if (error) throw error;
          analyses.push(...data);
        }
      }
      
      // If no analyses found for selected files, get all user analyses as fallback
      if (analyses.length === 0) {
        const { data, error } = await supabase
          .from('user_csv_analyses')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(20); // Limit to recent 20 to avoid overwhelming
          
        if (error) throw error;
        analyses = data || [];
        
        if (analyses.length > 0) {
          console.log(`📊 Showing ${analyses.length} saved configurations (all user configs - no matches for selected files)`);
        }
      } else {
        console.log(`📊 Showing ${analyses.length} saved configurations for selected files`);
      }
      
      return analyses.map(item => ({
        ...item,
        params: item.params as CsvAnalysis['params']
      })) as CsvAnalysis[];
    },
    enabled: !!user?.id,
  });

  // Helper function to resolve number from multiple possible column names
  const resolveNumber = (row: any, keys: string[]): number => {
    for (const key of keys) {
      const value = row[key];
      if (value !== undefined && value !== null && value !== '') {
        const parsed = parseDecimal(value);
        if (!isNaN(parsed)) {
          return parsed;
        }
      }
    }
    return 0;
  };

  // Helper function to resolve string from multiple possible column names
  const resolveString = (row: any, keys: string[]): string => {
    for (const key of keys) {
      const value = row[key];
      if (value !== undefined && value !== null && value !== '') {
        return value.toString().trim();
      }
    }
    return '';
  };

  // Parse CSV data and get raw original data for export
  const [originalCsvRows, setOriginalCsvRows] = useState<any[]>([]);
  const [originalCsvHeaders, setOriginalCsvHeaders] = useState<string[]>([]);

  const parseCsvData = (fileData: any[], filename?: string): ParsedCsvRow[] => {
    if (!fileData || !Array.isArray(fileData)) return [];

    // Store original data for export
    setOriginalCsvRows(fileData);
    if (fileData.length > 0) {
      setOriginalCsvHeaders(Object.keys(fileData[0]));
    }

    return fileData.map((row: any) => {
      const signal = row['Signal'] || row['signal'] || '';
      const type = row['Type'] || row['type'] || (signal.toLowerCase().includes('close') ? `Exit ${signal.toLowerCase().includes('short') ? 'short' : 'long'}` : '');
      
      // Try multiple P&L column names
      const pnlKeys = ['Net P&L USD', 'Net P&L USDT', 'P&L USD', 'P&L USDT', 'Net P&L', 'P&L', 'pnl'];
      const pnl = resolveNumber(row, pnlKeys);
      
      const dateStr = row['Date/Time'] || row['datetime'] || row['timestamp'] || '';
      
      // Try multiple price column names  
      const priceKeys = ['Price USD', 'Price USDT', 'Price', 'price'];
      const price = resolveNumber(row, priceKeys);
      
      // Try multiple position size column names
      const positionKeys = ['Position size (qty)', 'Position size qty', 'Position size', 'Qty', 'position_size'];
      const positionSizeNum = resolveNumber(row, positionKeys);
      
      // Get raw position size string for exact filtering
      let positionSizeRaw = '';
      for (const key of positionKeys) {
        if (row[key] !== undefined && row[key] !== null && row[key] !== '') {
          positionSizeRaw = row[key].toString();
          break;
        }
      }
      
      // Get position value or calculate it
      const positionValueKeys = ['Position size (value)', 'Position value', 'position_value'];
      let positionValue = resolveNumber(row, positionValueKeys);
      if (positionValue === 0 && price > 0 && positionSizeNum > 0) {
        positionValue = price * positionSizeNum;
      }

      // Extract symbol from CSV columns or filename
      const symbolKeys = ['Symbol', 'symbol', 'Ticker', 'ticker', 'Market', 'market', 'Instrument', 'instrument', 'Pair', 'pair'];
      let symbol = resolveString(row, symbolKeys);
      
      // If no symbol found in CSV, try to extract from filename
      if (!symbol && filename) {
        const filenameMatch = filename.match(/^([A-Z]{6,8})/i);
        if (filenameMatch) {
          symbol = filenameMatch[1].toUpperCase();
        }
      }

      // Optional columns that might exist in CSV
      const runupUsdKeys = ['Run-up USD', 'Runup USD', 'runup_usd'];
      const runupUsd = resolveNumber(row, runupUsdKeys);
      
      const runupPercentKeys = ['Run-up %', 'Runup %', 'runup_percent'];
      const runupPercent = resolveNumber(row, runupPercentKeys);
      
      const drawdownUsdKeys = ['Drawdown USD', 'drawdown_usd'];
      const drawdownUsd = resolveNumber(row, drawdownUsdKeys);
      
      const drawdownPercentKeys = ['Drawdown %', 'drawdown_percent'];
      const drawdownPercent = resolveNumber(row, drawdownPercentKeys);

      const cumulativeUsdKeys = ['Cumulative P&L USD', 'Cumulative P&L USDT', 'Cumulative P&L', 'cumulative_pnl'];
      const cumulativeUsd = resolveNumber(row, cumulativeUsdKeys);
      
      const cumulativePercentKeys = ['Cumulative P&L %', 'cumulative_percent'];
      const cumulativePercent = resolveNumber(row, cumulativePercentKeys);

      return {
        timestamp: dateStr,
        pnl,
        signal,
        type,
        price,
        position_size: positionSizeNum,
        position_size_raw: positionSizeRaw,
        position_value: positionValue > 0 ? positionValue : undefined,
        runup_usd: runupUsd !== 0 ? runupUsd : undefined,
        runup_percent: runupPercent !== 0 ? runupPercent : undefined,
        drawdown_usd: drawdownUsd !== 0 ? drawdownUsd : undefined,
        drawdown_percent: drawdownPercent !== 0 ? drawdownPercent : undefined,
        cumulative_pnl: cumulativeUsd !== 0 ? cumulativeUsd : undefined,
        cumulative_percent: cumulativePercent !== 0 ? cumulativePercent : undefined,
        symbol: symbol || undefined,
      };
    });
  };

  // Upload CSV file
  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      if (!user?.id) throw new Error('User not authenticated');

      // Parse CSV first for validation
      const text = await file.text();
      const parsed = Papa.parse(text, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (header) => header.trim(),
      });

      if (parsed.errors.length > 0) {
        console.warn('CSV parsing errors:', parsed.errors);
      }

      const data = parsed.data as any[];
      if (data.length === 0) {
        throw new Error('CSV file is empty');
      }

      // Check for required columns
      const headers = Object.keys(data[0]);
      const hasDateColumn = headers.some(h => 
        ['Date/Time', 'datetime', 'timestamp', 'time'].some(pattern => 
          h.toLowerCase().includes(pattern.toLowerCase())
        )
      );
      const hasPnlColumn = headers.some(h => 
        ['P&L', 'pnl', 'Net P&L'].some(pattern => 
          h.toLowerCase().includes(pattern.toLowerCase())
        )
      );
      const hasPositionColumn = headers.some(h => 
        ['Position size', 'Qty', 'quantity', 'position_size'].some(pattern => 
          h.toLowerCase().includes(pattern.toLowerCase())
        )
      );

      if (!hasDateColumn || !hasPnlColumn || !hasPositionColumn) {
        throw new Error('CSV must contain Date/Time, P&L, and Position size columns');
      }

      // Upload to storage
      const fileName = `${user.id}/${crypto.randomUUID()}-${file.name}`;
      const { error: uploadError } = await supabase.storage
        .from('user-csv')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Save metadata
      const { data: uploadData, error: dbError } = await supabase
        .from('user_csv_uploads')
        .insert({
          user_id: user.id,
          filename: file.name,
          file_size: file.size,
          storage_path: fileName,
          headers,
          sample_rows: data.slice(0, 3),
          project_id: selectedProjectId
        })
        .select()
        .single();

      if (dbError) throw dbError;
      return uploadData;
    },
    onSuccess: () => {
      toast.success('CSV file uploaded successfully');
      queryClient.invalidateQueries({ queryKey: ['csv-uploads'] });
    },
    onError: (error) => {
      console.error('Upload error:', error);
      toast.error(error instanceof Error ? error.message : 'Upload failed');
    }
  });

  // Delete CSV file
  const deleteMutation = useMutation({
    mutationFn: async (upload: CsvUpload) => {
      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('user-csv')
        .remove([upload.storage_path]);

      if (storageError) throw storageError;

      // Delete from database (cascade will handle analyses)
      const { error: dbError } = await supabase
        .from('user_csv_uploads')
        .delete()
        .eq('id', upload.id);

      if (dbError) throw dbError;
    },
    onSuccess: (_, upload) => {
      toast.success('File deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['csv-uploads'] });
      // Remove deleted file from selections
      if (selectedFiles.has(upload.id)) {
        const newSelection = new Set(selectedFiles);
        newSelection.delete(upload.id);
        setSelectedFiles(newSelection);
      }
      if (selectedForDeletion.has(upload.id)) {
        const newSelection = new Set(selectedForDeletion);
        newSelection.delete(upload.id);
        setSelectedForDeletion(newSelection);
      }
    },
    onError: (error) => {
      console.error('Delete error:', error);
      toast.error('Failed to delete file');
    }
  });

  // Bulk delete files
  const bulkDeleteMutation = useMutation({
    mutationFn: async (uploads: CsvUpload[]) => {
      const results = { success: 0, failed: 0, errors: [] as string[] };
      
      for (const upload of uploads) {
        try {
          // Delete from storage
          const { error: storageError } = await supabase.storage
            .from('user-csv')
            .remove([upload.storage_path]);

          if (storageError) throw storageError;

          // Delete from database (cascade will handle analyses)
          const { error: dbError } = await supabase
            .from('user_csv_uploads')
            .delete()
            .eq('id', upload.id);

          if (dbError) throw dbError;
          
          results.success++;
        } catch (error) {
          results.failed++;
          results.errors.push(`${upload.filename}: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
      }
      
      return results;
    },
    onSuccess: (results, uploads) => {
      const totalFiles = uploads.length;
      
      if (results.success === totalFiles) {
        toast.success(`Successfully deleted ${results.success} files`);
      } else if (results.success > 0) {
        toast(`Deleted ${results.success} of ${totalFiles} files. ${results.failed} failed.`);
      } else {
        toast.error(`Failed to delete all ${totalFiles} files`);
      }
      
      if (results.errors.length > 0) {
        console.error('Bulk delete errors:', results.errors);
      }
      
      queryClient.invalidateQueries({ queryKey: ['csv-uploads'] });
      
      // Clear selections for deleted files
      const deletedIds = new Set(uploads.map(u => u.id));
      setSelectedFiles(prev => new Set([...prev].filter(id => !deletedIds.has(id))));
      setSelectedForDeletion(new Set());
      setShowBulkDeleteDialog(false);
    },
    onError: (error) => {
      console.error('Bulk delete error:', error);
      toast.error('Failed to delete files');
      setShowBulkDeleteDialog(false);
    }
  });

  // Bulk delete helpers
  const toggleSelectForDeletion = (uploadId: string) => {
    const newSelection = new Set(selectedForDeletion);
    if (newSelection.has(uploadId)) {
      newSelection.delete(uploadId);
    } else {
      newSelection.add(uploadId);
    }
    setSelectedForDeletion(newSelection);
  };

  const toggleSelectAllForDeletion = () => {
    if (selectedForDeletion.size === uploads.length) {
      setSelectedForDeletion(new Set());
    } else {
      setSelectedForDeletion(new Set(uploads.map(u => u.id)));
    }
  };

  const handleBulkDelete = () => {
    const filesToDelete = uploads.filter(u => selectedForDeletion.has(u.id));
    bulkDeleteMutation.mutate(filesToDelete);
  };

  // Load saved parameters when files are selected
  useEffect(() => {
    if (selectedFiles.size > 0 && uploads.length > 0) {
      // Load saved parameters for each selected file
      for (const fileId of selectedFiles) {
        if (!perFileParams.has(fileId)) {
          const savedParams = loadFileParams(fileId);
          if (savedParams) {
            setPerFileParams(prev => new Map(prev).set(fileId, savedParams));
          }
        }
      }
    }
  }, [selectedFiles, uploads]);

  // Load CSV data when files selected
  useEffect(() => {
    if (selectedFiles.size === 0) {
      setCsvData([]);
      setUniquePositionSizes([]);
      setSelectedPositionSizes(new Set());
      setFileDataMap(new Map());
      return;
    }

    const loadMultipleCsvData = async () => {
      try {
        const newFileDataMap = new Map<string, ParsedCsvRow[]>();
        let allCombinedData: ParsedCsvRow[] = [];

        for (const fileId of selectedFiles) {
          const upload = uploads.find(u => u.id === fileId);
          if (!upload) continue;

          const { data: fileData, error } = await supabase.storage
            .from('user-csv')
            .download(upload.storage_path);

          if (error) throw error;

          const text = await fileData.text();
          const parsed = Papa.parse(text, {
            header: true,
            skipEmptyLines: true,
            transformHeader: (header) => header.trim(),
          });

          const parsedData = parseCsvData(parsed.data as any[], upload.filename);
          
          // Add file identifier to each row for tracking
          const dataWithFileId = parsedData.map(row => ({
            ...row,
            fileId,
            filename: upload.filename
          }));
          
          newFileDataMap.set(fileId, dataWithFileId);
          allCombinedData.push(...dataWithFileId);
        }

        // Sort combined data chronologically
        allCombinedData.sort((a, b) => {
          const dateA = new Date(a.timestamp).getTime();
          const dateB = new Date(b.timestamp).getTime();
          return dateA - dateB;
        });

        setFileDataMap(newFileDataMap);
        setCsvData(allCombinedData);

        // Get unique position sizes for each file
        const filePositionSizes = new Map<string, string[]>();
        for (const [fileId, data] of newFileDataMap) {
          const fileSizes = [...new Set(
            data.filter(row => row.position_size > 0)
              .map(row => row.position_size_raw)
          )].sort((a, b) => parseDecimal(a) - parseDecimal(b));
          filePositionSizes.set(fileId, fileSizes);
        }
        setPerFilePositionSizes(filePositionSizes);

        // Get all unique position sizes from all files
        const uniqueSizes = [...new Set(
          allCombinedData
            .filter(row => row.position_size > 0)
            .map(row => row.position_size_raw)
        )].sort((a, b) => parseDecimal(a) - parseDecimal(b));
        
        // Quick verification log: show up to 10 samples of raw -> parsed mapping
        const samples = uniqueSizes.slice(0, Math.min(10, uniqueSizes.length));
        if (samples.length > 0) {
          console.log('🔎 Position size parsing samples:', samples.map(s => `${s} -> ${parseDecimal(s)}`));
        }
        
        setUniquePositionSizes(uniqueSizes);
        setSelectedPositionSizes(new Set(uniqueSizes)); // Select all by default
      } catch (error) {
        console.error('Error loading CSV data:', error);
        toast.error('Failed to load CSV data');
      }
    };

    loadMultipleCsvData();
  }, [selectedFiles, uploads]);

  // Run analysis
  const runAnalysis = () => {
    if (csvData.length === 0 || selectedPositionSizes.size === 0) {
      toast.error('Please select files and position sizes to analyze');
      return;
    }

    setIsAnalysisRunning(true);

    try {
      // Debug tracking variables
      const selectedFileIds = Array.from(selectedFiles);
      let processedFilesCount = 0;
      const skippedFiles: Array<{filename: string, reason: string, details?: any}> = [];
      const processedFiles: Array<{filename: string, tradesCount: number}> = [];

      console.log(`🔍 Starting analysis for ${selectedFileIds.length} selected files:`, selectedFileIds.map(id => {
        const upload = uploads.find(u => u.id === id);
        return upload?.filename || 'Unknown';
      }));

      // Process each file with its own parameters
      const allProcessedTrades: any[] = [];
      
      for (const fileId of selectedFiles) {
        const fileParams = getFileParams(fileId);
        const fileData = fileDataMap.get(fileId) || [];
        const upload = uploads.find(u => u.id === fileId);
        const filename = upload?.filename || 'Unknown';
        
        console.log(`📁 Processing file: ${filename}`);
        console.log(`  - Raw data rows: ${fileData.length}`);
        console.log(`  - Selected position sizes: [${fileParams.selected_qty_values.join(', ')}]`);
        
        // Check if file has data
        if (fileData.length === 0) {
          const reason = 'No CSV data loaded for this file';
          skippedFiles.push({filename, reason});
          console.warn(`⚠️ ${filename}: ${reason}`);
          continue;
        }

        // Check position sizes configuration
        if (!fileParams.selected_qty_values || fileParams.selected_qty_values.length === 0) {
          const reason = 'No position sizes selected';
          skippedFiles.push({filename, reason, details: {available_position_sizes: [...new Set(fileData.map(row => row.position_size_raw))].slice(0, 5)}});
          console.warn(`⚠️ ${filename}: ${reason}`);
          continue;
        }
        
        // Filter by selected position sizes for this file
        const filteredFileData = fileData.filter(row => 
          fileParams.selected_qty_values.includes(row.position_size_raw)
        );

        console.log(`  - After position size filter: ${filteredFileData.length} rows`);
        
        if (filteredFileData.length === 0) {
          const availablePositions = [...new Set(fileData.map(row => row.position_size_raw))];
          const reason = 'No trades match selected position sizes';
          skippedFiles.push({
            filename, 
            reason,
            details: {
              selected_positions: fileParams.selected_qty_values,
              available_positions: availablePositions.slice(0, 10) // Limit to first 10 for readability
            }
          });
          console.warn(`⚠️ ${filename}: ${reason}`);
          console.warn(`  Selected: [${fileParams.selected_qty_values.join(', ')}]`);
          console.warn(`  Available: [${availablePositions.slice(0, 10).join(', ')}]`);
          continue;
        }
        
        // Sort by timestamp
        const sortedFileData = [...filteredFileData].sort((a, b) => {
          const dateA = new Date(a.timestamp).getTime();
          const dateB = new Date(b.timestamp).getTime();
          return dateA - dateB;
        });
        
        // Apply loss streak rule if enabled for this file
        let processedFileData = sortedFileData;
        if (fileParams.loss_streak_enabled && fileParams.max_consecutive_losses > 0) {
          processedFileData = applyLossStreakRule(sortedFileData, fileParams.max_consecutive_losses, fileParams.resume_after_win);
          console.log(`  - After loss streak rule: ${processedFileData.filter(t => !t.isSkipped).length} active trades`);
        }
        
        // Apply anti loss streak rule if enabled for this file
        if (fileParams.anti_loss_streak_enabled && fileParams.wait_for_losses > 0 && fileParams.stop_after_losses > 0) {
          processedFileData = AntiLossStreakService.applyAntiLossStreakFromParams(processedFileData, fileParams);
          console.log(`  - After anti loss streak rule: ${processedFileData.filter(t => !t.isSkipped).length} active trades`);
        }

        // Apply win protect rule if enabled for this file
        if (fileParams.win_protect_enabled && fileParams.win_protect_start_wins > 0 && fileParams.win_protect_max_wins > 0) {
          processedFileData = applyWinProtectRule(
            processedFileData, 
            fileParams.win_protect_start_wins, 
            fileParams.win_protect_max_wins, 
            fileParams.win_protect_include_nth_win,
            fileParams.win_protect_mode || 'consecutive_trades'
          );
          console.log(`  - After win protect rule: ${processedFileData.filter(t => !t.isSkipped).length} active trades`);
        }

        // Check if any trades remain active after rules
        const activeFileTrades = processedFileData.filter(trade => !trade.isSkipped);
        if (activeFileTrades.length === 0) {
          const reason = 'All trades filtered out by loss streak/anti-loss streak/win protect rules';
          skippedFiles.push({
            filename, 
            reason,
            details: {
              total_trades_before_rules: sortedFileData.length,
              loss_streak_enabled: fileParams.loss_streak_enabled,
              win_protect_enabled: fileParams.win_protect_enabled
            }
          });
          console.warn(`⚠️ ${filename}: ${reason}`);
          continue;
        }

        // Normalize P&L based on file-specific TP/SL and quantity
        const normalizedFileData = processedFileData.map(trade => {
          // Apply position size multiplier
          const multiplier = fileParams.position_size_multiplier || 1.0;
          const multipliedPositionSize = trade.position_size * multiplier;
          
          let normalizedPnl = trade.pnl;
          
          if (fileParams.tp_usd > 0 || fileParams.sl_usd > 0) {
            const pq = fileParams.quantity_base > 0 ? (multipliedPositionSize / fileParams.quantity_base) : 1;
            
            if (trade.pnl > 0 && fileParams.tp_usd > 0) {
              normalizedPnl = fileParams.tp_usd * pq;
            } else if (trade.pnl < 0 && fileParams.sl_usd > 0) {
              normalizedPnl = -fileParams.sl_usd * pq;
            }
          }

          return {
            ...trade,
            position_size: multipliedPositionSize, // Store multiplied position size
            normalized_pnl: normalizedPnl,
            filename: filename,
            fileId: fileId // Add fileId for tracking
          };
        });
        
        allProcessedTrades.push(...normalizedFileData);
        processedFilesCount++;
        processedFiles.push({filename, tradesCount: activeFileTrades.length});
        console.log(`✅ ${filename}: Successfully processed with ${activeFileTrades.length} active trades`);
      }

      // Log summary
      console.log(`\n📊 ANALYSIS SUMMARY:`);
      console.log(`  - Selected files: ${selectedFileIds.length}`);
      console.log(`  - Successfully processed: ${processedFilesCount}`);
      console.log(`  - Skipped files: ${skippedFiles.length}`);
      console.log(`  - Total trades: ${allProcessedTrades.filter(t => !t.isSkipped).length}`);

      // Show user feedback about processing results
      if (processedFilesCount > 0) {
        toast.success(`✅ Processed ${processedFilesCount}/${selectedFileIds.length} files with ${allProcessedTrades.filter(t => !t.isSkipped).length} total trades`);
      }
      
      if (skippedFiles.length > 0) {
        console.warn(`\n⚠️ SKIPPED FILES DETAILS:`);
        skippedFiles.forEach(({filename, reason, details}) => {
          console.warn(`  - ${filename}: ${reason}`);
          if (details) {
            console.warn(`    Details:`, details);
          }
        });
        
        toast.warning(
          `${skippedFiles.length} file(s) were skipped. Common issues: No position sizes selected, no matching trades, or all trades filtered by rules. Check console for details.`,
          { duration: 8000 }
        );
      }

      if (allProcessedTrades.length === 0) {
        if (skippedFiles.length > 0) {
          toast.error(
            `Analysis failed: All ${selectedFileIds.length} files were skipped. Most common issues: No position sizes selected, no matching trades, or restrictive loss/win rules. Check console for detailed breakdown.`,
            { duration: 10000 }
          );
        } else {
          toast.error('No trades match the selected criteria');
        }
        setIsAnalysisRunning(false);
        return;
      }

      // Sort all processed trades chronologically
      const normalizedTrades = allProcessedTrades.sort((a, b) => {
        const dateA = new Date(a.timestamp).getTime();
        const dateB = new Date(b.timestamp).getTime();
        return dateA - dateB;
      });

      // Filter out skipped trades for equity calculation
      const activeTrades = normalizedTrades.filter(trade => !trade.isSkipped);
      
      // Calculate equity curve with file contributions using global initial capital
      let cumulativePnl = 0;
      let peak = globalDefaults.initial_capital;
      let maxDrawdown = 0;
      const filePnlTracker = new Map<string, number>();

      const equityData = activeTrades.map(trade => {
        cumulativePnl += trade.normalized_pnl;
        const equity = globalDefaults.initial_capital + cumulativePnl;
        
        peak = Math.max(peak, equity);
        const drawdown = peak - equity;
        maxDrawdown = Math.max(maxDrawdown, drawdown);

        // Track contributions by file
        const fileId = (trade as any).fileId || 'Unknown';
        const filename = (trade as any).filename || fileId;
        if (!filePnlTracker.has(filename)) {
          filePnlTracker.set(filename, 0);
        }
        filePnlTracker.set(filename, filePnlTracker.get(filename)! + trade.normalized_pnl);

        // Create bot_contributions object for chart
        const bot_contributions: Record<string, number> = {};
        if (selectedFiles.size > 1) {
          bot_contributions[filename] = trade.normalized_pnl;
        } else {
          bot_contributions['CSV'] = trade.normalized_pnl;
        }

        return {
          timestamp: trade.timestamp,
          cumulative_pnl: cumulativePnl,
          total_pnl: trade.normalized_pnl,
          bot_contributions
        };
      });

      // Calculate statistics (only for active trades)
      const totalTrades = activeTrades.length;
      const winningTrades = activeTrades.filter(t => t.normalized_pnl > 0).length;
      const losingTrades = activeTrades.filter(t => t.normalized_pnl < 0).length;
      const winRate = totalTrades > 0 ? (winningTrades / totalTrades) * 100 : 0;

      const wins = activeTrades.filter(t => t.normalized_pnl > 0);
      const losses = activeTrades.filter(t => t.normalized_pnl < 0);
      
      const avgWin = wins.length > 0 ? wins.reduce((sum, t) => sum + t.normalized_pnl, 0) / wins.length : 0;
      const avgLoss = losses.length > 0 ? Math.abs(losses.reduce((sum, t) => sum + t.normalized_pnl, 0) / losses.length) : 0;
      
      const profitFactor = avgLoss > 0 ? avgWin / avgLoss : 0;
      const riskRewardRatio = avgLoss > 0 ? avgWin / avgLoss : 0;
      const maxDrawdownPercent = globalDefaults.initial_capital > 0 ? (maxDrawdown / globalDefaults.initial_capital) * 100 : 0;

      // Prepare trading history with calculated cumulative values (including skipped trades for display)
      let runningCumulative = 0;
      const tradingHistory = normalizedTrades.map((trade, index) => {
        // Only include active trades in cumulative calculation
        if (!trade.isSkipped) {
          runningCumulative += trade.normalized_pnl;
        }
        
        const netPnlPercent = trade.position_value ? (trade.normalized_pnl / trade.position_value) * 100 : 0;
        const cumulativePercent = globalDefaults.initial_capital > 0 ? (runningCumulative / globalDefaults.initial_capital) * 100 : 0;

        return {
          ...trade,
          normalized_pnl: trade.normalized_pnl,
          cumulative_pnl: runningCumulative,
          cumulative_percent: cumulativePercent,
          net_pnl_percent: netPnlPercent,
          index: index + 1
        };
      });

      // Calculate file contributions with detailed statistics
      const fileContributions: FileContribution[] = [];
      
      for (const fileId of selectedFiles) {
        const upload = uploads.find(u => u.id === fileId);
        const fileParams = getFileParams(fileId);
        const filename = upload?.filename || 'Unknown';
        
        // Get all trades for this file (both active and skipped)
        const fileTrades = normalizedTrades.filter(trade => (trade as any).fileId === fileId);
        const activeFileTrades = fileTrades.filter(trade => !trade.isSkipped);
        
        if (activeFileTrades.length === 0) continue;
        
        // Calculate basic statistics
        const totalPnl = activeFileTrades.reduce((sum, trade) => sum + trade.normalized_pnl, 0);
        const winningTrades = activeFileTrades.filter(t => t.normalized_pnl > 0);
        const losingTrades = activeFileTrades.filter(t => t.normalized_pnl < 0);
        const winRate = activeFileTrades.length > 0 ? (winningTrades.length / activeFileTrades.length) * 100 : 0;
        
        const avgWin = winningTrades.length > 0 ? winningTrades.reduce((sum, t) => sum + t.normalized_pnl, 0) / winningTrades.length : 0;
        const avgLoss = losingTrades.length > 0 ? Math.abs(losingTrades.reduce((sum, t) => sum + t.normalized_pnl, 0) / losingTrades.length) : 0;
        const profitFactor = avgLoss > 0 ? avgWin / avgLoss : 0;
        
        // Calculate max TP/SL from actual normalized PnL values
        const maxTP = winningTrades.length > 0 ? Math.max(...winningTrades.map(t => t.normalized_pnl)) : 0;
        const maxSL = losingTrades.length > 0 ? Math.abs(Math.min(...losingTrades.map(t => t.normalized_pnl))) : 0;
        
        // Calculate file-specific drawdown
        let filePeak = fileParams.initial_capital || globalDefaults.initial_capital;
        let fileMaxDrawdown = 0;
        let runningFileEquity = filePeak;
        
        for (const trade of activeFileTrades) {
          runningFileEquity += trade.normalized_pnl;
          filePeak = Math.max(filePeak, runningFileEquity);
          const drawdown = filePeak - runningFileEquity;
          fileMaxDrawdown = Math.max(fileMaxDrawdown, drawdown);
        }
        
        const fileMaxDrawdownPercent = filePeak > 0 ? (fileMaxDrawdown / filePeak) * 100 : 0;
        
        // Get date range
        const sortedFileTrades = [...activeFileTrades].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
        const dateRange = {
          start: sortedFileTrades.length > 0 ? sortedFileTrades[0].timestamp : '',
          end: sortedFileTrades.length > 0 ? sortedFileTrades[sortedFileTrades.length - 1].timestamp : ''
        };
        
        fileContributions.push({
          filename,
          fileId,
          totalPnl,
          tradeCount: activeFileTrades.length,
          winningTrades: winningTrades.length,
          losingTrades: losingTrades.length,
          winRate,
          avgWin,
          avgLoss,
          profitFactor,
          maxTP,
          maxSL,
          maxDrawdown: fileMaxDrawdown,
          maxDrawdownPercent: fileMaxDrawdownPercent,
          dateRange
        });
      }

      setAnalysisResults({
        equityData,
        statistics: {
          totalTrades,
          winningTrades,
          losingTrades,
          winRate,
          totalPnl: cumulativePnl,
          avgWin,
          avgLoss,
          profitFactor,
          maxDrawdown,
          maxDrawdownPercent,
          riskRewardRatio
        },
        fileContributions,
        tradingHistory
      });

      // Final success message with processing summary
      if (processedFilesCount === selectedFileIds.length) {
        toast.success(`✅ Analysis completed! All ${processedFilesCount} files processed successfully with ${allProcessedTrades.filter(t => !t.isSkipped).length} total trades.`);
      } else {
        toast.success(
          `⚠️ Analysis completed! ${processedFilesCount}/${selectedFileIds.length} files processed (${skippedFiles.length} skipped). Total trades: ${allProcessedTrades.filter(t => !t.isSkipped).length}`,
          { duration: 8000 }
        );
      }
    } catch (error) {
      console.error('Analysis error:', error);
      toast.error('Analysis failed');
    } finally {
      setIsAnalysisRunning(false);
    }
  };

  // Apply loss streak rule to filter trades
  const applyLossStreakRule = (trades: ParsedCsvRow[], maxLosses: number, resumeAfterWin: boolean): ParsedCsvRow[] => {
    const result: ParsedCsvRow[] = [];
    let consecutiveLosses = 0;
    let isPaused = false;
    
    for (const trade of trades) {
      const isWin = trade.pnl > 0;
      const isLoss = trade.pnl < 0;
      
      if (isPaused) {
        if (isWin) {
          // End pause state
          isPaused = false;
          consecutiveLosses = 0;
          
          if (resumeAfterWin) {
            // Skip the winning trade that ends the pause
            result.push({
              ...trade,
              isSkipped: true,
              skipReason: 'Resume after win'
            });
          } else {
            // Include the winning trade that ends the pause
            result.push({
              ...trade,
              isSkipped: false
            });
          }
        } else {
          // Still paused, skip this trade
          result.push({
            ...trade,
            isSkipped: true,
            skipReason: 'Loss streak pause'
          });
        }
      } else {
        // Not paused, include trade
        result.push({
          ...trade,
          isSkipped: false
        });
        
        // Update loss streak counter
        if (isLoss) {
          consecutiveLosses++;
          if (consecutiveLosses >= maxLosses) {
            isPaused = true;
          }
        } else if (isWin) {
          consecutiveLosses = 0;
        }
      }
    }
    
    return result;
  };

  // Apply win protect rule to filter trades
  const applyWinProtectRule = (trades: ParsedCsvRow[], startWins: number, maxWins: number, includeNthWin: boolean, mode: 'consecutive_trades' | 'consecutive_losses'): ParsedCsvRow[] => {
    const result: ParsedCsvRow[] = [];
    let consecutiveWins = 0;
    let consecutiveLosses = 0;
    let state: 'WAITING' | 'OPEN' | 'PAUSED' = 'WAITING';
    let winsInOpen = 0;
    let winsInPaused = 0; // Counter for wins during PAUSED state
    
    for (const trade of trades) {
      // Skip if already skipped by previous rules
      if (trade.isSkipped) {
        result.push(trade);
        continue;
      }
      
      const isWin = trade.pnl > 0;
      const isLoss = trade.pnl < 0;
      
      if (state === 'WAITING') {
        if (isWin) {
          consecutiveWins++;
          if (consecutiveWins === startWins) {
            // This is the Nth win - always skip it and transition to OPEN
            result.push({
              ...trade,
              isSkipped: true,
              skipReason: `Win protect: Nth win (${startWins}) reached - opening from next trade`
            });
            state = 'OPEN';
            winsInOpen = 0;
            consecutiveLosses = 0;
          } else {
            // Still waiting for enough consecutive wins - skip trade
            result.push({
              ...trade,
              isSkipped: true,
              skipReason: `Win protect: Waiting for ${startWins} wins (${consecutiveWins}/${startWins})`
            });
          }
        } else {
          // Loss resets the waiting counter - skip trade
          consecutiveWins = 0;
          consecutiveLosses = 0;
          result.push({
            ...trade,
            isSkipped: true,
            skipReason: 'Win protect: Reset by loss, waiting for wins'
          });
        }
      } else if (state === 'OPEN') {
        if (mode === 'consecutive_trades') {
          // Original mode: count wins, stop after M wins
          if (isWin) {
            winsInOpen++;
            
            if (winsInOpen === maxWins) {
              // This is the Mth win - handle based on includeNthWin setting
              if (includeNthWin) {
                result.push({ ...trade, isSkipped: false });
              } else {
                result.push({
                  ...trade,
                  isSkipped: true,
                  skipReason: `Win protect: Mth win (${maxWins}) excluded`
                });
              }
              // Transition to PAUSED after Mth win
              state = 'PAUSED';
              winsInPaused = 0; // Reset wins counter when entering PAUSED
            } else {
              // Still collecting wins before reaching M - include this trade
              result.push({ ...trade, isSkipped: false });
            }
          } else if (isLoss) {
            // Loss in OPEN state - include this loss and reset to WAITING
            result.push({ ...trade, isSkipped: false });
            state = 'WAITING';
            consecutiveWins = 0;
            consecutiveLosses = 0;
            winsInOpen = 0;
          } else {
            // Breakeven - include but doesn't affect state
            result.push({ ...trade, isSkipped: false });
          }
        } else {
          // New mode: consecutive_losses - count losses, stop after M consecutive losses
          if (isWin) {
            // Win resets the loss counter but stays in OPEN
            consecutiveLosses = 0;
            result.push({ ...trade, isSkipped: false });
            console.log(`[Win Protect] Trade ${trade.symbol} | Result: WIN | State: ${state} | ConsecLosses: ${consecutiveLosses}/${maxWins}`);
          } else if (isLoss) {
            // Execute the loss trade first
            result.push({ ...trade, isSkipped: false });
            consecutiveLosses++;
            console.log(`[Win Protect] Trade ${trade.symbol} | Result: LOSS | State: ${state} | ConsecLosses: ${consecutiveLosses}/${maxWins}`);
            
            if (consecutiveLosses >= maxWins) {
              // Reached M consecutive losses AFTER executing this loss
              // Transition to PAUSED for next trades
              state = 'PAUSED';
              winsInPaused = 0; // Reset wins counter when entering PAUSED
              console.log(`[Win Protect] Reached ${maxWins} consecutive losses - transitioning to PAUSED`);
            }
          } else {
            // Breakeven - doesn't affect loss counter
            result.push({ ...trade, isSkipped: false });
            console.log(`[Win Protect] Trade ${trade.symbol} | Result: BE | State: ${state} | ConsecLosses: ${consecutiveLosses}/${maxWins}`);
          }
        }
      } else if (state === 'PAUSED') {
        if (isWin) {
          // Skip win trade and count it
          winsInPaused++;
          result.push({
            ...trade,
            isSkipped: true,
            skipReason: `Win protect: Paused - counting wins (${winsInPaused}/${startWins})`
          });
          console.log(`[Win Protect] Trade ${trade.symbol} | Result: WIN in PAUSED | WinsInPaused: ${winsInPaused}/${startWins}`);
          
          if (winsInPaused >= startWins) {
            // Reached N wins while paused - transition to OPEN
            state = 'OPEN';
            winsInPaused = 0;
            consecutiveLosses = 0;
            consecutiveWins = 0;
            winsInOpen = 0;
            console.log(`[Win Protect] Reached ${startWins} wins in PAUSED - transitioning to OPEN`);
          }
        } else if (isLoss) {
          // Loss during pause - skip and reset win counter, STAY in PAUSED
          winsInPaused = 0;
          result.push({
            ...trade,
            isSkipped: true,
            skipReason: 'Win protect: Loss during pause - resetting win counter'
          });
          console.log(`[Win Protect] Trade ${trade.symbol} | Result: LOSS in PAUSED - reset win counter, staying in PAUSED`);
        } else {
          // Breakeven - skip without affecting counter
          result.push({
            ...trade,
            isSkipped: true,
            skipReason: 'Win protect: Paused - breakeven trade'
          });
          console.log(`[Win Protect] Trade ${trade.symbol} | Result: BE in PAUSED | WinsInPaused: ${winsInPaused}/${startWins}`);
        }
      }
    }
    
    return result;
  };

  // Save analysis configuration
  const saveAnalysis = async () => {
    if (!user?.id || selectedFiles.size === 0) return;

    try {
      const name = prompt('Enter a name for this analysis configuration:');
      if (!name) return;

      // Save global defaults and per-file parameters
      const configData = {
        globalDefaults,
        perFileParams: Object.fromEntries(perFileParams),
        selectedFiles: Array.from(selectedFiles)
      };

      // Save configuration for the combination
      await supabase.from('user_csv_analyses').insert({
        user_id: user.id,
        upload_id: Array.from(selectedFiles)[0], // Use first file as primary
        name: selectedFiles.size > 1 ? `${name} (Multi-file)` : name,
        params: configData as any
      });

      toast.success('Analysis configuration saved');
      queryClient.invalidateQueries({ queryKey: ['csv-analyses'] });
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save analysis configuration');
    }
  };

  // Load saved analysis
  const loadSavedAnalysis = (analysis: CsvAnalysis) => {
    const params = analysis.params as any;
    
    // Check if it's a new multi-file format
    if (params.globalDefaults && params.perFileParams) {
      setGlobalDefaults(params.globalDefaults);
      
      const fileParamsMap = new Map<string, AnalysisParams>();
      Object.entries(params.perFileParams).forEach(([fileId, fileParams]) => {
        fileParamsMap.set(fileId, fileParams as AnalysisParams);
      });
      setPerFileParams(fileParamsMap);
      
      if (params.selectedFiles) {
        setSelectedFiles(new Set(params.selectedFiles));
      }
    } else {
      // Legacy format - apply to global defaults
      const legacyParams: AnalysisParams = {
        tp_usd: params.tp_usd || 60,
        sl_usd: params.sl_usd || 120,
        quantity_base: params.quantity_base || 200,
        initial_capital: params.initial_capital || 10000,
        selected_qty_values: params.selected_qty_values || [],
        loss_streak_enabled: params.loss_streak_enabled || false,
        max_consecutive_losses: params.max_consecutive_losses || 3,
        resume_after_win: params.resume_after_win || false,
        win_protect_enabled: params.win_protect_enabled || false,
        win_protect_start_wins: params.win_protect_start_wins || 3,
        win_protect_max_wins: params.win_protect_max_wins || 5,
        win_protect_include_nth_win: params.win_protect_include_nth_win || false,
        win_protect_mode: (params as any).win_protect_mode || 'consecutive_trades',
        anti_loss_streak_enabled: params.anti_loss_streak_enabled || false,
        wait_for_losses: params.wait_for_losses || 3,
        stop_after_losses: params.stop_after_losses || 2,
        reset_on_win: params.reset_on_win !== false, // Default to true
        anti_loss_streak_mode: (params as any).anti_loss_streak_mode || 'consecutive_losses',
        // Add new percentage mode fields with defaults
        use_percentage_mode: false,
        tp_percent: 2.0,
        sl_percent: 2.0,
        auto_parse_filename: true,
        // Add position size multiplier with default
        position_size_multiplier: 1.0,
      };
      setGlobalDefaults(legacyParams);
      setSelectedPositionSizes(new Set(legacyParams.selected_qty_values));
    }
    
    toast.success(`Loaded configuration: ${analysis.name}`);
  };

  // Helper function to extract instrument from filename
  const extractInstrument = (filename: string): string => {
    // Pattern: "EURUSD - 0.2" or "ETHUSDT - 1.8 - 5s" 
    const match = filename.match(/^([A-Z]{6,8})/);
    return match ? match[1] : 'UNKNOWN';
  };

  // Helper function to add metadata columns to CSV data
  const addMetadataColumns = (rows: any[], fileParams: any, filename: string) => {
    const instrument = extractInstrument(filename);
    
    // Get base values from file params or global defaults
    const tpUsd = fileParams?.tp_usd || globalDefaults.tp_usd;
    const slUsd = fileParams?.sl_usd || globalDefaults.sl_usd;
    const baseQuantity = fileParams?.quantity_base || globalDefaults.quantity_base;
    const multiplier = fileParams?.position_size_multiplier || 1.0;
    
    // Calculate adjusted values based on multiplier
    const adjustedQuantityBase = baseQuantity * multiplier;
    const adjustedTpUsd = tpUsd * multiplier;  // ✅ Adjusted TP
    const adjustedSlUsd = slUsd * multiplier;  // ✅ Adjusted SL
    
    // Log sample for verification (only first row)
    if (rows.length > 0) {
      console.log('📦 Export metadata calculation:', {
        filename,
        baseQuantity,
        multiplier,
        adjustedQuantityBase,
        tpUsd: `${tpUsd} → ${adjustedTpUsd}`,
        slUsd: `${slUsd} → ${adjustedSlUsd}`
      });
    }
    
    return rows.map(row => ({
      ...row,
      __TP_USD__: adjustedTpUsd,               // ✅ Adjusted value
      __SL_USD__: adjustedSlUsd,               // ✅ Adjusted value
      __QUANTITY_BASE__: adjustedQuantityBase, // ✅ Adjusted value
      __POSITION_SIZE_MULTIPLIER__: multiplier, // ✅ New field
      __INSTRUMENT__: instrument,
      __CSV_ANALYSIS_CONFIG__: 'true'
    }));
  };

  // Export filtered CSV
  const exportFilteredCsv = () => {
    if (!originalCsvRows.length || !selectedPositionSizes.size) {
      toast.error('No data to export');
      return;
    }

    try {
      // Find position size column in original headers
      const positionKeys = ['Position size (qty)', 'Position size qty', 'Position size', 'Qty', 'position_size'];
      const positionColumnName = originalCsvHeaders.find(header => 
        positionKeys.some(key => header.toLowerCase().includes(key.toLowerCase()))
      );

      if (!positionColumnName) {
        toast.error('Position size column not found');
        return;
      }

      // Filter original rows by selected position sizes (exact string matching)
      const filteredRows = originalCsvRows.filter(row => {
        const positionValue = row[positionColumnName];
        if (positionValue === undefined || positionValue === null || positionValue === '') {
          return false;
        }
        return selectedPositionSizes.has(positionValue.toString());
      });

      if (filteredRows.length === 0) {
        toast.error('No rows match the selected position sizes');
        return;
      }

      // Add metadata columns for Bot Merging integration
      let enhancedRows = filteredRows;
      if (selectedFiles.size === 1) {
        const firstFileId = Array.from(selectedFiles)[0];
        const firstUpload = uploads.find(u => u.id === firstFileId);
        const fileParams = perFileParams.get(firstFileId);
        
        if (firstUpload && fileParams) {
          enhancedRows = addMetadataColumns(filteredRows, fileParams, firstUpload.filename);
        }
      }

      // Generate CSV with enhanced headers and filtered rows
      const enhancedHeaders = [...originalCsvHeaders];
      if (selectedFiles.size === 1) {
        enhancedHeaders.push('__TP_USD__', '__SL_USD__', '__QUANTITY_BASE__', '__POSITION_SIZE_MULTIPLIER__', '__INSTRUMENT__', '__CSV_ANALYSIS_CONFIG__');
      }

      const csvContent = Papa.unparse({
        fields: enhancedHeaders,
        data: enhancedRows
      });

      // Download the file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      
      // Generate filename according to user's specification
      let filename = 'filtered_analysis';
      
      if (selectedFiles.size === 1) {
        // Get original filename and file-specific data
        const firstFileId = Array.from(selectedFiles)[0];
        const firstUpload = uploads.find(u => u.id === firstFileId);
        const fileParams = perFileParams.get(firstFileId);
        const filePositionSizes = perFilePositionSizes.get(firstFileId) || [];
        
        if (firstUpload && fileParams) {
          // Clean filename: remove extension and " - List of trades" suffix
          filename = firstUpload.filename
            .replace(/\.[^/.]+$/, '')
            .replace(/ - List of trades$/, '');
          
          // Use file-specific selected position sizes, fallback to all if empty
          const fileSelectedSizes = fileParams.selected_qty_values.length > 0 
            ? fileParams.selected_qty_values 
            : filePositionSizes;
          
          // Sort selected sizes from small to large
          const selectedSizes = fileSelectedSizes.sort((a, b) => parseDecimal(a) - parseDecimal(b));
          const sizesSuffix = selectedSizes.join('_');
          
          // Sort file's unique position sizes to ensure correct order and find indices
          const sortedFilePositionSizes = [...filePositionSizes].sort((a, b) => parseDecimal(a) - parseDecimal(b));
          
          // Find 1-based indices of first and last selected sizes
          const firstIndex = sortedFilePositionSizes.findIndex(size => size === selectedSizes[0]) + 1;
          const lastIndex = sortedFilePositionSizes.findIndex(size => size === selectedSizes[selectedSizes.length - 1]) + 1;
          
          // Create final filename: [Original]_[Sizes]_[FirstIndex]_[LastIndex]
          filename = `${filename}_${sizesSuffix}_${firstIndex}_${lastIndex}`;
        }
      } else {
        filename = 'multi_file_filtered_analysis';
        toast.info('Multiple files selected. Only single-file export generates specific filename format.');
      }
      
      // Create final filename
      const finalFilename = `${filename}.csv`;
      
      link.setAttribute('download', finalFilename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(`Exported ${filteredRows.length} rows to CSV`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export CSV');
    }
  };

  // Export individual files as ZIP
  const exportIndividualFiles = async () => {
    if (!fileDataMap.size || selectedFiles.size === 0) {
      toast.error('No data or files to export');
      return;
    }

    if (selectedFiles.size === 1) {
      toast.info('For single file export, use "Export Filtered CSV" button');
      return;
    }

    setIsExportingIndividual(true);

    try {
      const zip = new JSZip();
      let exportedCount = 0;

      // Process each selected file
      for (const fileId of selectedFiles) {
        const upload = uploads.find(u => u.id === fileId);
        const fileParams = perFileParams.get(fileId);
        const filePositionSizes = perFilePositionSizes.get(fileId) || [];
        const fileData = fileDataMap.get(fileId);

        if (!upload || !fileParams || !fileData) {
          console.warn(`Missing data for file ${fileId}`);
          continue;
        }

        // Use file-specific selected position sizes, fallback to all if empty
        const fileSelectedSizes = fileParams.selected_qty_values.length > 0 
          ? fileParams.selected_qty_values 
          : filePositionSizes;

        if (fileSelectedSizes.length === 0) {
          console.warn(`No position sizes selected for file ${upload.filename}`);
          continue;
        }

        // Convert file data back to original CSV format for export
        const originalCsvData = fileData.map(row => {
          const csvRow: any = {};
          
          // Map back to original CSV column names
          csvRow['Date/Time'] = row.timestamp;
          csvRow['P&L USD'] = row.pnl;
          csvRow['Signal'] = row.signal;
          csvRow['Type'] = row.type;
          csvRow['Price'] = row.price;
          csvRow['Position size (qty)'] = row.position_size_raw;
          
          if (row.position_value !== undefined) {
            csvRow['Position size (value)'] = row.position_value;
          }
          if (row.runup_usd !== undefined) {
            csvRow['Run-up USD'] = row.runup_usd;
          }
          if (row.runup_percent !== undefined) {
            csvRow['Run-up %'] = row.runup_percent;
          }
          if (row.drawdown_usd !== undefined) {
            csvRow['Drawdown USD'] = row.drawdown_usd;
          }
          if (row.drawdown_percent !== undefined) {
            csvRow['Drawdown %'] = row.drawdown_percent;
          }
          if (row.cumulative_pnl !== undefined) {
            csvRow['Cumulative P&L USD'] = row.cumulative_pnl;
          }
          if (row.cumulative_percent !== undefined) {
            csvRow['Cumulative P&L %'] = row.cumulative_percent;
          }
          
          return csvRow;
        });

        // Add metadata columns for Bot Merging integration
        const enhancedCsvData = addMetadataColumns(originalCsvData, fileParams, upload.filename);

        // Filter rows by selected position sizes for this file
        const filteredRows = enhancedCsvData.filter(row => {
          const positionValue = row['Position size (qty)'];
          if (positionValue === undefined || positionValue === null || positionValue === '') {
            return false;
          }
          return fileSelectedSizes.some(size => size === positionValue.toString());
        });

        if (filteredRows.length === 0) {
          console.warn(`No matching rows for file ${upload.filename}`);
          continue;
        }

        // Generate filename according to existing rules
        let filename = upload.filename
          .replace(/\.[^/.]+$/, '')
          .replace(/ - List of trades$/, '');

        // Sort selected sizes from small to large
        const selectedSizes = [...fileSelectedSizes].sort((a, b) => parseDecimal(a) - parseDecimal(b));
        const sizesSuffix = selectedSizes.join('_');

        // Sort file's unique position sizes to ensure correct order and find indices
        const sortedFilePositionSizes = [...filePositionSizes].sort((a, b) => parseDecimal(a) - parseDecimal(b));

        // Find 1-based indices of first and last selected sizes
        const firstIndex = sortedFilePositionSizes.findIndex(size => size === selectedSizes[0]) + 1;
        const lastIndex = sortedFilePositionSizes.findIndex(size => size === selectedSizes[selectedSizes.length - 1]) + 1;

        // Create final filename: [Original]_[Sizes]_[FirstIndex]_[LastIndex]
        const finalFilename = `${filename}_${sizesSuffix}_${firstIndex}_${lastIndex}.csv`;

        // Generate CSV content with proper headers
        const headers = Object.keys(filteredRows[0] || {});
        const csvContent = Papa.unparse({
          fields: headers,
          data: filteredRows
        });

        // Add to ZIP
        zip.file(finalFilename, csvContent);
        exportedCount++;
      }

      if (exportedCount === 0) {
        toast.error('No files could be exported');
        return;
      }

      // Generate ZIP blob
      const zipBlob = await zip.generateAsync({ type: 'blob' });

      // Create download
      const link = document.createElement('a');
      const url = URL.createObjectURL(zipBlob);
      link.setAttribute('href', url);
      
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      const zipFilename = `CSV_Analysis_${timestamp}.zip`;
      
      link.setAttribute('download', zipFilename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(`Exported ${exportedCount} individual files to ${zipFilename}`);
    } catch (error) {
      console.error('Individual export error:', error);
      toast.error('Failed to export individual files');
    } finally {
      setIsExportingIndividual(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      {/* Project Manager Sidebar */}
      <div className="lg:col-span-1">
        <CsvProjectManager
          selectedProjectId={selectedProjectId}
          onProjectSelect={setSelectedProjectId}
        />
      </div>

      <div className="lg:col-span-3 space-y-6">
        {/* Upload Section */}
        <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload size={20} />
            Upload & Manage CSV Files
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-3">
            <input
              type="file"
              accept=".csv"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  uploadMutation.mutate(file);
                }
              }}
              className="hidden"
              id="csv-upload"
            />
            <div className="flex items-center gap-3">
              <Button
                onClick={() => document.getElementById('csv-upload')?.click()}
                disabled={uploadMutation.isPending}
              >
                <Upload size={16} className="mr-2" />
                {uploadMutation.isPending ? 'Uploading...' : 'Upload New CSV'}
              </Button>
              
              {selectedProjectId && (
                <div className="text-sm text-muted-foreground">
                  Uploading to current project
                </div>
              )}
            </div>
          </div>

          {uploads.length > 0 && (
            <div className="space-y-3">
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium">
                    {selectedProjectId 
                      ? `Project Files (${uploads.length})` 
                      : `My Uploads (${uploads.length})`
                    }
                  </h4>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="select-all-delete"
                        checked={selectedForDeletion.size === uploads.length}
                        onCheckedChange={toggleSelectAllForDeletion}
                      />
                      <Label htmlFor="select-all-delete" className="text-sm cursor-pointer">
                        Select All
                      </Label>
                    </div>
                    {selectedForDeletion.size > 0 && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => setShowBulkDeleteDialog(true)}
                        disabled={bulkDeleteMutation.isPending}
                      >
                        <Trash2 size={14} className="mr-1" />
                        Delete Selected ({selectedForDeletion.size})
                      </Button>
                    )}
                  </div>
                </div>
                
                {/* File Project Selector */}
                <FileProjectSelector
                  selectedFiles={selectedForDeletion}
                  onFilesMove={() => {
                    setSelectedForDeletion(new Set());
                    queryClient.invalidateQueries({ queryKey: ['csv-uploads'] });
                  }}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-96 overflow-y-auto custom-scrollbar pr-2">
                {uploads.map((upload, index) => {
                  const isSelected = selectedFiles.has(upload.id);
                  const hasParams = loadFileParams(upload.id) !== null;
                  const colorIndex = index % 6;
                  const colorSchemes = [
                    'border-primary/30 bg-primary/5 hover:bg-primary/10',
                    'border-accent/40 bg-accent/8 hover:bg-accent/15',
                    'border-success/30 bg-success/5 hover:bg-success/10',
                    'border-warning/30 bg-warning/5 hover:bg-warning/10',
                    'border-secondary/40 bg-secondary/8 hover:bg-secondary/15',
                    'border-muted-foreground/20 bg-muted/50 hover:bg-muted/70'
                  ];
                  const colorScheme = colorSchemes[colorIndex];
                  
                  return (
                    <div 
                      key={upload.id} 
                      className={`
                        relative flex items-center justify-between p-4 rounded-lg border-2 transition-all duration-300 group
                        ${isSelected 
                          ? 'border-primary bg-primary/10 shadow-lg shadow-primary/20 scale-[1.02]' 
                          : `${colorScheme} hover:shadow-md hover:scale-[1.01]`
                        }
                      `}
                    >
                      {isSelected && (
                        <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5 rounded-lg pointer-events-none" />
                      )}
                      
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <Checkbox
                          checked={selectedForDeletion.has(upload.id)}
                          onCheckedChange={() => toggleSelectForDeletion(upload.id)}
                          className="flex-shrink-0"
                        />
                        <div className={`
                          p-2 rounded-lg transition-colors duration-300
                          ${isSelected 
                            ? 'bg-primary/20 text-primary' 
                            : 'bg-muted/50 text-muted-foreground group-hover:bg-muted'
                          }
                        `}>
                          <FileText size={18} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className={`font-medium truncate ${isSelected ? 'text-primary-foreground' : 'text-foreground'}`}>
                              {upload.filename}
                            </p>
                            {hasParams && (
                              <div className="px-2 py-1 bg-success/20 text-success text-xs rounded-full font-medium">
                                Saved
                              </div>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            {formatCurrency(upload.file_size / 1024)} KB • {new Date(upload.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 ml-2">
                        <Button
                          variant={isSelected ? "default" : "outline"}
                          size="sm"
                          onClick={() => {
                            const newSelection = new Set(selectedFiles);
                            if (selectedFiles.has(upload.id)) {
                              newSelection.delete(upload.id);
                            } else {
                              newSelection.add(upload.id);
                            }
                            setSelectedFiles(newSelection);
                          }}
                          className={`transition-all duration-300 ${
                            isSelected 
                              ? 'shadow-md hover:shadow-lg' 
                              : 'hover:border-primary/50 hover:text-primary'
                          }`}
                        >
                          {isSelected ? 'Selected' : 'Select'}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => deleteMutation.mutate(upload)}
                          disabled={deleteMutation.isPending}
                          className="hover:border-destructive/50 hover:text-destructive transition-colors duration-300"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
              {selectedFiles.size > 0 && (
                <div className="mt-3 p-3 bg-muted/50 rounded-lg">
                  <p className="text-sm font-medium">
                    Selected {selectedFiles.size} file{selectedFiles.size > 1 ? 's' : ''} for multi-file analysis
                  </p>
                  <div className="flex gap-2 mt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedFiles(new Set(uploads.map(u => u.id)))}
                    >
                      Select All
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedFiles(new Set())}
                    >
                      Clear Selection
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* TP/SL Percentage Calculator */}
      {selectedFiles.size > 0 && (
        <PercentageCalculatorPanel
          filenames={Array.from(selectedFiles).map(fileId => {
            const upload = uploads.find(u => u.id === fileId);
            return upload?.filename || 'Unknown';
          })}
          onCalculationComplete={(results) => {
            console.log('TP/SL calculations completed:', results);
            
            // Auto-fill TP/SL USD values from calculation results
            results.forEach((result, resultKey) => {
              // Match calculation result with file by filename
              const matchingFile = uploads.find(upload => {
                return resultKey.includes(upload.filename) || upload.filename.includes(resultKey);
              });
              
              if (matchingFile && result.tpUSD && result.slUSD) {
                const currentParams = getFileParams(matchingFile.id);
                updateFileParams(matchingFile.id, {
                  ...currentParams,
                  tp_usd: result.tpUSD,
                  sl_usd: result.slUSD
                });
                
                console.log(`✅ Auto-filled TP/SL for ${matchingFile.filename}: TP=${result.tpUSD}, SL=${result.slUSD}`);
              }
            });
            
            if (results.size > 0) {
              toast(`Auto-filled TP/SL USD values for ${results.size} files from percentage calculations`);
            }
          }}
          onAutoApplyMinPositionSize={() => {
            // Auto-apply min position sizes to all files after calculation
            let successCount = 0;
            
            for (const fileId of selectedFiles) {
              const fileData = fileDataMap.get(fileId);
              if (!fileData || fileData.length === 0) continue;
              
              const positionSizes = fileData.map((row: any) => row.position_size).filter(size => size > 0);
              if (positionSizes.length === 0) continue;
              
              const minPositionSize = Math.min(...positionSizes);
              const currentParams = getFileParams(fileId);
              updateFileParams(fileId, {
                ...currentParams,
                quantity_base: minPositionSize
              });
              successCount++;
            }
            
            if (successCount > 0) {
              toast(`Auto-applied minimum position sizes to ${successCount} files`);
            }
          }}
        />
       )}

      {/* CSV Configuration Upload */}
      {selectedFiles.size > 0 && csvData.length > 0 && (
        <CsvConfigUpload
          config={csvConfig}
          onConfigLoaded={setCsvConfig}
        />
      )}

      {/* File Parameters Configuration */}
      {selectedFiles.size > 0 && csvData.length > 0 && (
        <>
          <FileParametersPanel
            files={Array.from(selectedFiles).map(fileId => {
              const upload = uploads.find(u => u.id === fileId);
              const hasSaved = loadFileParams(fileId) !== null;
              return { 
                id: fileId, 
                filename: upload?.filename || 'Unknown',
                hasSavedParams: hasSaved,
                data: fileDataMap.get(fileId) || []
              };
            })}
            perFileParams={perFileParams}
            globalDefaults={globalDefaults}
            uniquePositionSizes={perFilePositionSizes}
            csvConfig={csvConfig}
            onUpdateFileParams={updateFileParams}
            onUpdateGlobalDefaults={updateGlobalDefaults}
            onCopyToAll={copyParamsToAll}
            onClearSavedParams={(fileId) => {
              const upload = uploads.find(u => u.id === fileId);
              if (upload) {
                const key = getFileParamsKey(upload);
                localStorage.removeItem(key);
                setPerFileParams(prev => {
                  const newMap = new Map(prev);
                  newMap.delete(fileId);
                  return newMap;
                });
                toast.success('Saved parameters cleared');
              }
            }}
            onBulkApplyMultiplier={handleBulkApplyMultiplier}
          />

            <PositionSequenceFinder
              csvData={csvData}
              availablePositions={uniquePositionSizes}
              globalDefaults={globalDefaults}
              getFileParams={getFileParams}
              onApplySequence={handleApplySequence}
              selectedFiles={selectedFiles}
              uploads={uploads}
            />

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Play className="h-5 w-5" />
                Run Analysis
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Button
                  onClick={runAnalysis}
                  disabled={isAnalysisRunning}
                >
                  <Play size={16} className="mr-2" />
                  {isAnalysisRunning ? 'Running Analysis...' : 'Run Analysis'}
                </Button>
                
                {analysisResults && (
                  <>
                    <Button variant="outline" onClick={saveAnalysis}>
                      <Download size={16} className="mr-2" />
                      Save Configuration
                    </Button>
                    <Button variant="outline" onClick={exportFilteredCsv}>
                      <Download size={16} className="mr-2" />
                      Export Filtered CSV
                    </Button>
                    {selectedFiles.size > 1 && (
                      <Button 
                        variant="outline" 
                        onClick={exportIndividualFiles}
                        disabled={isExportingIndividual}
                      >
                        <Package size={16} className="mr-2" />
                        {isExportingIndividual ? 'Exporting...' : 'Export Individual Files'}
                      </Button>
                    )}
                  </>
                )}
              </div>

              {savedAnalyses.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-medium">
                    Saved Configurations ({savedAnalyses.length})
                    {(() => {
                      // Check if any analysis matches selected files
                      const hasSelectedFileMatch = savedAnalyses.some(analysis => 
                        selectedFiles.has(analysis.upload_id)
                      );
                      return hasSelectedFileMatch ? 
                        <span className="text-xs text-muted-foreground font-normal"> - for selected files</span> :
                        <span className="text-xs text-muted-foreground font-normal"> - all your configs</span>;
                    })()}
                  </h4>
                  <div className="grid gap-2 max-h-32 overflow-y-auto">
                    {savedAnalyses.map((analysis) => (
                      <div key={analysis.id} className="flex items-center justify-between p-2 border rounded">
                        <div>
                          <p className="font-medium text-sm">{analysis.name}</p>
                          <p className="text-xs text-muted-foreground">
                            Created: {new Date(analysis.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => loadSavedAnalysis(analysis)}
                        >
                          Load
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Results */}
      {analysisResults && (
        <div className="space-y-6">
          <BotMergeAnalysisStats statistics={analysisResults.statistics} />
          
          <BotMergeAnalysisChart
            data={analysisResults.equityData}
            title={selectedFiles.size > 1 ? "Multi-File Equity Curve (TP/SL Normalized)" : "Equity Curve (TP/SL Normalized)"}
            initialCapital={globalDefaults.initial_capital}
            interactiveMode={true}
          />
          
          {analysisResults.fileContributions && analysisResults.fileContributions.length > 0 && (
            <IndividualFilePerformance fileContributions={analysisResults.fileContributions} />
          )}
          
          <TradingHistoryTable data={analysisResults.tradingHistory} />
        </div>
      )}

      {/* Bulk Delete Confirmation Dialog */}
      <AlertDialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Delete Files Confirmation
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>Are you sure you want to delete <strong>{selectedForDeletion.size}</strong> files?</p>
              <p className="text-sm text-muted-foreground">
                This will permanently delete the files and all related analyses. This action cannot be undone.
              </p>
              {selectedForDeletion.size > 0 && (
                <div className="mt-3 p-3 bg-muted/50 rounded-md">
                  <p className="text-xs font-medium mb-2">Files to be deleted:</p>
                  <ul className="text-xs space-y-1 max-h-32 overflow-y-auto">
                    {Array.from(selectedForDeletion).slice(0, 10).map(id => {
                      const upload = uploads.find(u => u.id === id);
                      return (
                        <li key={id} className="text-muted-foreground">
                          • {upload?.filename || 'Unknown file'}
                        </li>
                      );
                    })}
                    {selectedForDeletion.size > 10 && (
                      <li className="text-muted-foreground font-medium">
                        ... and {selectedForDeletion.size - 10} more files
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkDeleteMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              disabled={bulkDeleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {bulkDeleteMutation.isPending ? 'Deleting...' : `Delete ${selectedForDeletion.size} Files`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </div>
    </div>
  );
};
